import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { Course, ReportData, PaginationResponse } from 'types';

import { apiQuery } from '../../query';

import {
  ListRequest,
  ListResponse,
  UpdateRequest,
  CreateRequest,
  ReportRequest,
  DetailsRequest,
  CourseResponse,
  ReportResponse,
  DuplicateRequest,
  UsedVersionsRequest,
  CourseVersionsResponse,
} from './types';

const courseTagType = 'COURSES';
const courseListTagId = 'LIST';

const reportTagType = 'COURSES_REPORT';

export const coursesApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'coursesApi',
  tagTypes: [courseTagType, reportTagType],
  endpoints: (build) => ({
    delete: build.mutation<void, string>({
      invalidatesTags: [{ type: courseTagType, id: courseListTagId }],
      query: (id) => ({
        method: 'delete',
        url: URL.COURSE_DETAILS.replace(':id', id),
      }),
    }),

    create: build.mutation<Course, CreateRequest>({
      transformResponse: (response: CourseResponse) => response.data,
      invalidatesTags: [{ type: courseTagType, id: courseListTagId }],
      query: (data) => ({
        data,
        method: 'post',
        url: URL.COURSE_LIST,
      }),
    }),

    details: build.query<Course, DetailsRequest>({
      transformResponse: (response: CourseResponse) => response.data,
      providesTags: (_result, _error, { id }) => [{ id, type: courseTagType }],
      query: ({ id }) => ({
        method: 'get',
        url: generatePath(URL.COURSE_DETAILS, { id }),
      }),
    }),

    duplicate: build.mutation<Course, DuplicateRequest>({
      transformResponse: (response: CourseResponse) => response.data,
      invalidatesTags: [{ type: courseTagType, id: courseListTagId }],
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.COURSE_DUPLICATE, { id }),
      }),
    }),

    report: build.query<ReportData, ReportRequest>({
      providesTags: [reportTagType],
      transformResponse: (response: ReportResponse) => response.data,
      query: ({ id, version, reportId, ...rest }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.COURSE_REPORT, { id, version, reportId }),
          rest
        ),
      }),
    }),

    autoTranslate: build.mutation<void, { id: string; force?: boolean }>({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: courseTagType },
        { type: courseTagType, id: courseListTagId },
      ],
      query: ({ id, force = false }) => ({
        method: 'post',
        data: { force },
        url: generatePath(URL.COURSE_AUTO_TRANSLATE, { id }),
      }),
    }),

    update: build.mutation<Course, UpdateRequest>({
      transformResponse: (response: CourseResponse) => response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.COURSE_DETAILS, { id }),
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: courseTagType },
        { type: courseTagType, id: courseListTagId },
      ],
    }),

    usedVersions: build.query<number[], UsedVersionsRequest>({
      providesTags: (_result, _error, { id }) => [{ id, type: courseTagType }],
      transformResponse: (response: CourseVersionsResponse) =>
        response.data.usedVersions,
      query: ({ id, includeActive }) => ({
        method: 'get',
        url: addParamsToUrl(generatePath(URL.COURSE_VERSIONS, { id }), {
          includeActive,
        }),
      }),
    }),

    list: build.query<PaginationResponse<Course>, ListRequest>({
      transformResponse: (response: ListResponse) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.COURSE_LIST, params),
      }),
      providesTags: (result) => [
        { type: courseTagType, id: courseListTagId },
        ...((result?.data ?? []).map(({ id }) => ({
          id,
          type: courseTagType,
        })) as { id: string; type: typeof courseTagType }[]),
      ],
    }),
  }),
});

export const {
  useListQuery,
  useReportQuery,
  useDetailsQuery,
  useLazyListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useLazyDetailsQuery,
  useUsedVersionsQuery,
  useDuplicateMutation,
  useAutoTranslateMutation,
} = coursesApi;

export type { ListRequest, UpdateRequest, ReportRequest };

export {
  courseTagType as coursesTagType,
  reportTagType as coursesReportTagType,
};
